/* SPEC-563 — "Arrange menu" (D1: a dialog, not a page). Opened from the rail's account menu
 * and from the sidebar's edit bar. It holds what the sidebar rows cannot: the screens you
 * have hidden (with Show), Reset to the standard menu, and your saved layouts (presets).
 *
 * Copy is in planner words: "screens", "folders", "layout". A preset is called a "saved
 * layout" on screen. A preset stores folders + hidden screens only (D6): applying one keeps
 * your starred screens.
 *
 * role="dialog" aria-modal, portalled to <body>, Escape or the backdrop closes it, focus
 * returns to the control that opened it — the ChangePasswordDialog pattern. */
import React from 'react';
import { createPortal } from 'react-dom';
import { EyeOff, X } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useWorkspace } from '@/lib/workspace';
import { VIEW_REGISTRY } from '@/lib/viewRegistry';
import { favouriteLabel, parseFavourite } from '@/config/nav-groups';
import {
  MAX_NAV_PRESETS,
  applyPreset,
  cleanLabel,
  show,
} from '@/config/nav-layout';
import { useNavLayout } from '@/hooks/useNavLayout';
import {
  createNavPreset,
  deleteNavPreset,
  fetchNavPresets,
  renameNavPreset,
  type NavPreset,
} from '@/lib/api';

const BTN = 'px-2.5 py-1.5 rounded-lg text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60';
const BTN_QUIET = `${BTN} text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-overlay)]`;
const BTN_SOLID = `${BTN} bg-amber-500 text-black font-medium hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed`;
const INPUT = 'min-w-0 flex-1 text-xs bg-[var(--bg-base)] border border-[var(--border-default)] rounded-lg px-2 py-1.5 text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-amber-500/40';

/** Mounted once in AppShell; renders the dialog while the shared layout says it is open. */
export function ArrangeMenuHost() {
  const nav = useNavLayout();
  const opener = React.useRef<HTMLElement | null>(null);
  React.useEffect(() => {
    if (nav.arrangeOpen) opener.current = document.activeElement as HTMLElement | null;
  }, [nav.arrangeOpen]);
  if (!nav.arrangeOpen) return null;
  return (
    <ArrangeMenuDialog
      onClose={() => {
        nav.closeArrange();
        // Back to the control that opened it (the account menu has closed by then, so its
        // trigger may be gone — then focus simply stays on the body).
        window.setTimeout(() => opener.current?.isConnected && opener.current.focus(), 0);
      }}
    />
  );
}

export function ArrangeMenuDialog({ onClose }: { onClose: () => void }) {
  const nav = useNavLayout();
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const [status, setStatus] = React.useState('');
  const [confirmReset, setConfirmReset] = React.useState(false);
  const panelRef = React.useRef<HTMLDivElement>(null);

  // `onClose` changes identity on every provider update; keep it in a ref so the effect
  // below runs ONCE. Re-running it moved focus back to the title after every Show or Use
  // (review finding 3).
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onCloseRef.current(); return; }
      // aria-modal promises the rest of the page is out of reach: keep Tab inside the dialog.
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select, [tabindex="0"]',
      ));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !panelRef.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !panelRef.current.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    panelRef.current?.querySelector<HTMLElement>('h2')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Only screens this login can open are listed: a hidden screen the role no longer has is
  // kept in the layout (it may come back) but showing it would change nothing on screen.
  const rolePages = user?.pages || [];
  const allowed = workspace?.pages ? rolePages.filter((p) => workspace.pages!.includes(p)) : rolePages;
  const pages = VIEW_REGISTRY.filter((p) => allowed.includes(p.path) && !nav.locked.includes(p.path));
  const hiddenRows = nav.layout.hidden
    .map((entry) => {
      const t = parseFavourite(entry);
      const page = t ? VIEW_REGISTRY.find((p) => p.path === t.path) : undefined;
      const tabs = page?.groups?.flatMap((g) => g.tabs.map((x) => x.key));
      return { entry, label: favouriteLabel(entry, pages, tabs) };
    })
    .filter((r): r is { entry: string; label: string } => r.label !== null);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="arrange-menu-title"
        className="relative w-full max-w-md max-h-[85vh] overflow-y-auto bg-[var(--bg-raised)] border border-[var(--border-subtle)] rounded-2xl shadow-2xl p-5 space-y-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="arrange-menu-title" tabIndex={-1} className="text-base font-display font-bold text-[var(--text-primary)] focus:outline-none">
              Arrange menu
            </h2>
            <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
              Your menu, your way. It changes what you see in the menu, never what you can open.
              Admins can see how your menu is arranged.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className={`${BTN_QUIET} p-1.5`}>
            <X className="w-4 h-4" aria-hidden />
          </button>
        </div>

        <p className="sr-only" aria-live="polite">{status}</p>

        <section aria-labelledby="arrange-hidden-title" className="space-y-2">
          <h3 id="arrange-hidden-title" className="text-sm font-semibold text-[var(--text-primary)]">Hidden screens</h3>
          {hiddenRows.length === 0 ? (
            <p className="text-xs text-[var(--text-tertiary)]">
              Nothing is hidden. To hide a screen, press the pencil beside the menu search, then “…” on the screen.
            </p>
          ) : (
            <ul className="space-y-1">
              {hiddenRows.map((r) => (
                <li key={r.entry} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg bg-[var(--bg-base)]">
                  <span className="flex items-center gap-2 text-sm text-[var(--text-secondary)] min-w-0">
                    <EyeOff className="w-3.5 h-3.5 flex-shrink-0 text-[var(--text-tertiary)]" aria-hidden />
                    <span className="truncate">{r.label}</span>
                  </span>
                  <button
                    type="button"
                    className={BTN_QUIET}
                    aria-label={`Show ${r.label} in the menu again`}
                    onClick={() => { nav.apply((l) => show(l, r.entry)); setStatus(`${r.label} is back in your menu, where it was.`); }}
                  >
                    Show
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <PresetsSection onStatus={setStatus} />

        <section aria-labelledby="arrange-reset-title" className="space-y-2 border-t border-[var(--border-subtle)] pt-4">
          <h3 id="arrange-reset-title" className="text-sm font-semibold text-[var(--text-primary)]">Standard menu</h3>
          <p className="text-xs text-[var(--text-tertiary)]">
            Grouped by what you are doing: Plan, Buy, Sell, Money, Trust. Your starred screens stay.
          </p>
          {confirmReset ? (
            <div className="flex items-center gap-2">
              <p className="text-xs text-[var(--text-secondary)] flex-1">Your folders and hidden screens will be replaced. Save them as a layout first if you want them back.</p>
              <button type="button" className={BTN_QUIET} onClick={() => setConfirmReset(false)}>Cancel</button>
              <button
                type="button"
                className={BTN_SOLID}
                onClick={() => { nav.reset(); setConfirmReset(false); setStatus('Menu reset to the standard menu.'); }}
              >
                Reset
              </button>
            </div>
          ) : (
            <button type="button" className={BTN_QUIET} disabled={!nav.customised} onClick={() => setConfirmReset(true)}>
              {nav.customised ? 'Reset to the standard menu…' : 'You are using the standard menu'}
            </button>
          )}
        </section>
      </div>
    </div>,
    document.body,
  );
}

/** Saved layouts (presets): save the current folders + hidden screens under a name, apply one
 *  back, rename, delete. At most MAX_NAV_PRESETS. Applying keeps the starred screens (D6). */
function PresetsSection({ onStatus }: { onStatus: (text: string) => void }) {
  const nav = useNavLayout();
  const [presets, setPresets] = React.useState<NavPreset[] | null>(null);
  const [error, setError] = React.useState('');
  const [name, setName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [editing, setEditing] = React.useState<{ id: string; text: string } | null>(null);
  const [confirm, setConfirm] = React.useState<{ id: string; kind: 'apply' | 'delete' } | null>(null);

  const load = React.useCallback(() => {
    fetchNavPresets()
      .then((res) => { setPresets(res?.presets ?? []); setError(''); })
      .catch(() => { setPresets([]); setError('Saved layouts could not be loaded just now.'); });
  }, []);
  React.useEffect(load, [load]);

  const run = async (fn: () => Promise<unknown>, message: string) => {
    setBusy(true);
    try {
      await fn();
      onStatus(message);
      setError('');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message.replace(/^\d{3}: /, '') : 'That did not work. Please try again.');
    } finally {
      setBusy(false);
      setEditing(null);
      setConfirm(null);
    }
  };

  const clean = cleanLabel(name);
  const full = (presets?.length ?? 0) >= MAX_NAV_PRESETS;

  return (
    <section aria-labelledby="arrange-presets-title" className="space-y-2 border-t border-[var(--border-subtle)] pt-4">
      <h3 id="arrange-presets-title" className="text-sm font-semibold text-[var(--text-primary)]">Saved layouts</h3>
      <p className="text-xs text-[var(--text-tertiary)]">
        Save your folders and hidden screens to come back to. Applying one keeps your starred screens.
      </p>
      {error && <p role="alert" className="text-xs text-red-400">{error}</p>}
      {presets === null ? (
        <p className="text-xs text-[var(--text-tertiary)]">Loading…</p>
      ) : presets.length === 0 ? (
        <p className="text-xs text-[var(--text-tertiary)]">No saved layouts yet.</p>
      ) : (
        <ul className="space-y-1">
          {presets.map((p) => (
            <li key={p.id} className="px-2 py-1.5 rounded-lg bg-[var(--bg-base)] space-y-1">
              {editing?.id === p.id ? (
                <form
                  className="flex items-center gap-1"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const n = cleanLabel(editing.text);
                    if (n) run(() => renameNavPreset(p.id, n), `Layout renamed to ${n}.`);
                  }}
                >
                  <label htmlFor={`preset-name-${p.id}`} className="sr-only">New name for {p.name}</label>
                  <input
                    id={`preset-name-${p.id}`}
                    autoFocus
                    maxLength={40}
                    value={editing.text}
                    onChange={(e) => setEditing({ id: p.id, text: e.target.value })}
                    className={INPUT}
                  />
                  <button type="button" className={BTN_QUIET} onClick={() => setEditing(null)}>Cancel</button>
                  <button type="submit" className={BTN_SOLID} disabled={busy || !cleanLabel(editing.text)}>Save</button>
                </form>
              ) : confirm?.id === p.id ? (
                <div className="flex items-center gap-1">
                  <p className="text-xs text-[var(--text-secondary)] flex-1">
                    {confirm.kind === 'apply'
                      ? `Use ${p.name}? Your current folders and hidden screens are replaced.`
                      : `Delete ${p.name}? Your menu does not change.`}
                  </p>
                  <button type="button" className={BTN_QUIET} onClick={() => setConfirm(null)}>Cancel</button>
                  {confirm.kind === 'apply' ? (
                    <button
                      type="button"
                      className={BTN_SOLID}
                      onClick={() => {
                        nav.apply((l) => applyPreset(l, p.preset));
                        setConfirm(null);
                        onStatus(`${p.name} applied to your menu.`);
                      }}
                    >
                      Use it
                    </button>
                  ) : (
                    <button type="button" className={BTN_SOLID} disabled={busy} onClick={() => run(() => deleteNavPreset(p.id), `${p.name} deleted.`)}>
                      Delete
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <span className="flex-1 min-w-0 truncate text-sm text-[var(--text-secondary)]">{p.name}</span>
                  <button type="button" className={BTN_QUIET} aria-label={`Use the layout ${p.name}`} onClick={() => setConfirm({ id: p.id, kind: 'apply' })}>Use</button>
                  <button type="button" className={BTN_QUIET} aria-label={`Rename the layout ${p.name}`} onClick={() => setEditing({ id: p.id, text: p.name })}>Rename</button>
                  <button type="button" className={BTN_QUIET} aria-label={`Delete the layout ${p.name}`} onClick={() => setConfirm({ id: p.id, kind: 'delete' })}>Delete</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex items-center gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          if (!clean || full) return;
          const { folders, hidden } = nav.layout;
          run(() => createNavPreset(clean, { folders, hidden }), `Your menu is saved as ${clean}.`).then(() => setName(''));
        }}
      >
        <label htmlFor="preset-new-name" className="sr-only">Name for this layout</label>
        <input
          id="preset-new-name"
          maxLength={40}
          placeholder={full ? `At most ${MAX_NAV_PRESETS} saved layouts` : 'Name this layout, for example Month end'}
          disabled={full}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={INPUT}
        />
        <button type="submit" className={BTN_SOLID} disabled={busy || !clean || full}>Save current menu</button>
      </form>
    </section>
  );
}
